import type { ForwardedRef } from "react";
import { forwardRef } from "react";

import HighCharts, { AST } from "highcharts";
// Ensure window._Highcharts is set before any UMD extension module evaluates.
// Rolldown's production build can strip the UMD IIFE that normally sets this global.
import "./highchartsGlobal";
// @ts-ignore - May lack declaration file depending on TS config; typed at usage via cast.
import NoDataToDisplay from "highcharts/es-modules/Extensions/NoDataToDisplay/NoDataToDisplay.js";
import HCMore from "highcharts/highcharts-more";
import HCAnnotations from "highcharts/modules/annotations";
import HCExporting from "highcharts/modules/exporting";
import HCHeatmap from "highcharts/modules/heatmap";
import HCMap from "highcharts/modules/map";
import HCPatternFill from "highcharts/modules/pattern-fill";
import HCSankey from "highcharts/modules/sankey";
import HCTreemap from "highcharts/modules/treemap";
import HCVenn from "highcharts/modules/venn";
import { HighchartsReact } from "highcharts-react-official";

// Explicitly initialize Highcharts modules to ensure features are registered
type HighchartsModule = ((H: typeof HighCharts) => void) | { default?: (H: typeof HighCharts) => void } | undefined;

const initializeHighchartsModule = (mod: HighchartsModule): void => {
    if (typeof mod === "function") {
        mod(HighCharts);
        return;
    }
    if (mod && typeof mod.default === "function") {
        mod.default(HighCharts);
    }
};

const highchartsModules: HighchartsModule[] = [
    HCMore as unknown as HighchartsModule,
    HCExporting as unknown as HighchartsModule,
    HCPatternFill as unknown as HighchartsModule,
    HCVenn as unknown as HighchartsModule,
    HCAnnotations as unknown as HighchartsModule,
    HCMap as unknown as HighchartsModule,
    HCSankey as unknown as HighchartsModule,
    HCHeatmap as unknown as HighchartsModule,
    HCTreemap as unknown as HighchartsModule,
];

highchartsModules.forEach(initializeHighchartsModule);

// Manually compose NoDataToDisplay with our HighCharts instance.
// The UMD wrapper (highcharts/modules/no-data-to-display) self-composes on an internal
// Highcharts reference that may differ from our imported instance in Vite's ESM context,
// so we import the raw ES module and call compose() on the correct instance.
(
    NoDataToDisplay as {
        compose: (chart: typeof HighCharts.Chart, options: ReturnType<typeof HighCharts.getOptions>) => void;
    }
).compose(HighCharts.Chart, HighCharts.getOptions());

AST.allowedReferences.push("data:");
AST.allowedAttributes.push("data-automation", "data-pdf-icon", "data-automation-icon-name", "viewBox", "xmlns");
AST.allowedTags.push("label");

export const Highcharts = HighCharts;

export const ReactHighcharts = forwardRef(
    (props: HighchartsReact.Props, ref: ForwardedRef<HighchartsReact.RefObject>) => (
        <HighchartsReact
            highcharts={HighCharts}
            constructorType="chart"
            {...props}
            ref={ref}
        />
    ),
);
// this is only required by highcharts-ng and will be removed!
// never read Highcharts from the window
(window as any)["Highcharts"] = HighCharts;

/**
 * Fix $.highcharts() broken when Highmaps is initialized
 * Register Highcharts as a plugin in jQuery
 */
if ((window as any)["jQuery"]) {
    (window as any)["jQuery"].fn.highcharts = function () {
        if (this[0]) {
            // this[0] is the renderTo div

            // When called without parameters or with the return argument,
            // return an existing chart
            return Highcharts.charts[this.attr("data-highcharts-chart")];
        }
    };
}
